import { Control } from '@Common/Controls/Controls';
import { DropDown } from '@Common/Controls/DropDown';
import { Input } from '@Common/Controls/Input';
import { TimeInterval, TypeKind } from '@Common/Enums';
import timeHelper, { dateTimeDuration } from '@Common/Helpers/TimeHelper';
import { DateTimeDuration } from '@InstanceFactory';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { px, toNumber, Units } from '@Common/Controls/Units';
import ShowTimeIntervalParam = timeHelper.ShowTimeIntervalParam;

export class DurationControlSetting extends ShowTimeIntervalParam {
    css?: object = null;
    className? = '';
    width?: Units = 'auto';
    height?: Units = 'auto';

    dropDownWidth?: Units = '194px';
    inputWidth?: Units = 50;
    inputRightMargin?: Units = 6;
    onChange? = _.noop;
    blockElement? = true;
}

export class DurationControl implements Control {
    private readonly events = new MulticastDelegateBag();
    public readonly onChange = this.events.create<IHandler>();
    public readonly onValidate = this.events.create<ICallback<boolean>>();
    private readonly root: JQuery<HTMLElement>;
    private readonly input: Input;
    private readonly dropDown: DropDown;
    private readonly inputWidth: number;
    private readonly inputRightMargin: number;

    constructor(private readonly settings: DurationControlSetting = {}) {
        _.defaults(settings, new DurationControlSetting());

        const { inputRightMargin, inputWidth } = this.settings;
        [this.inputRightMargin, this.inputWidth] = [inputRightMargin, inputWidth].map(toNumber);

        this.root = settings.blockElement ? $('<div class="duration"/>') : $('<span class="duration"/>');
        this.input = new Input({
            type: TypeKind.int,
            width: px(settings.inputWidth)
        });
        this.onChange.add(settings.onChange);
        this.input.getControl().appendTo(this.root).on('change', this.onChange.toFunction());
        this.input.onChange(this.onChange.toFunction());

        this.dropDown = new DropDown({
            width: settings.dropDownWidth,
            sortable: false
        });
        this.dropDown
            .getControl()
            .css({
                verticalAlign: 'top'
            })
            .addClass('inline')
            .appendTo(this.root);
        const timeIntervals = timeHelper.getTimeIntervals(settings);
        this.dropDown.setItems(timeIntervals);
        this.dropDown.onChange(this.onChange.toFunction());
    }

    public adjust(width, height) {
        if (width || height) {
            width && this.root.width(width);
            height && this.root.height(height);
        }
        this.input.adjust(this.inputWidth, height);
        this.dropDown.adjust(width - this.inputWidth - 1 - this.inputRightMargin, height);
    }

    public validate(): boolean {
        let isValid = true;
        this.input.removeError();
        try {
            isValid = parseInt(this.input.getValue()) > 0;
        } catch (e) {
            isValid = false;
        } finally {
            !isValid && this.input.setError(strings.durationControl.mustBeGreaterZero);
        }
        this.onValidate(isValid);
        return isValid;
    }

    public enabled(state?: boolean): boolean {
        if (!_.isUndefined(state)) {
            this.dropDown.setState(state);
            this.input.setState(state);
        }
        return this.dropDown.enabled();
    }

    getControl() {
        return this.root;
    }

    setValue(dateTimeDuration: DateTimeDuration): void {
        if (dateTimeDuration) {
            this.input.setValue(dateTimeDuration.intervalCount);
            this.dropDown.selectItemsById(dateTimeDuration.intervalType);
        }
    }

    getValue(): DateTimeDuration {
        const filterValues = this.dropDown.getValue();
        const intervalType = filterValues && filterValues.length > 0 ? filterValues[0].id : TimeInterval.default;
        return dateTimeDuration(intervalType, this.input.getValue());
    }

    setState(enabled: boolean): void {
        this.input.setState(enabled);
        this.dropDown.setState(enabled);
    }

    destroy() {
        this.events.destroy();
        this.dropDown.destroy();
        this.input.destroy();
        this.root.remove();
    }
}
